import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Field, inputCls } from "@/components/shared";

const FIELD_TYPES = ["text", "textarea", "number", "date", "dropdown", "checkbox", "file"];
const CATEGORIES = ["seller_details", "property_details", "buyer_criteria", "viewing_feedback", "custom"];
const newField = () => ({ id: crypto.randomUUID(), label: "", type: "text", required: false, options: [] });

export const FormBuilderDialog = ({ template, onClose, onSaved }) => {
  const { t } = useLang();
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || "custom");
  const [fields, setFields] = useState(
    template?.fields?.length ? template.fields.map((f) => ({ ...f })) : [newField()]
  );
  const [saving, setSaving] = useState(false);

  const typeLabel = {
    text: t("typeText"), textarea: t("typeTextarea"), number: t("typeNumber"), date: t("typeDate"),
    dropdown: t("typeDropdown"), checkbox: t("typeCheckbox"), file: t("typeFile"),
  };
  const catLabel = {
    seller_details: t("sellerDetailsCat"), property_details: t("propertyDetailsCat"),
    buyer_criteria: t("buyerCriteriaCat"), viewing_feedback: t("viewingFeedbackCat"), custom: t("customCat"),
  };

  const update = (i, patch) => setFields(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };

  const save = async () => {
    if (!name.trim()) return toast.error(t("required"));
    const cleaned = fields.filter((f) => f.label.trim());
    if (!cleaned.length) return toast.error(t("required"));
    setSaving(true);
    try {
      const payload = { name: name.trim(), category, fields: cleaned };
      if (template?.id) await api.put(`/form_templates/${template.id}`, payload);
      else await api.post("/form_templates", payload);
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
            {template?.id ? `${t("edit")} — ${template.name}` : t("newForm")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <Field label={t("formName")}>
            <input data-testid="form-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t("category")}>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="form-category-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{catLabel[c]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="space-y-3 mt-4">
          {fields.map((f, i) => (
            <div key={f.id} data-testid={`form-field-row-${i}`} className="bg-stone-50 border border-stone-200 rounded-lg p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-col gap-0.5">
                  <button data-testid={`field-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0}
                    className="text-stone-400 hover:text-stone-800 disabled:opacity-30 p-0.5"><ArrowUp size={13} /></button>
                  <button data-testid={`field-down-${i}`} onClick={() => move(i, 1)} disabled={i === fields.length - 1}
                    className="text-stone-400 hover:text-stone-800 disabled:opacity-30 p-0.5"><ArrowDown size={13} /></button>
                </div>
                <input
                  data-testid={`field-label-input-${i}`}
                  className={`${inputCls} flex-1 min-w-[140px]`}
                  placeholder={t("fieldLabel")}
                  value={f.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                />
                <Select value={f.type} onValueChange={(v) => update(i, { type: v })}>
                  <SelectTrigger data-testid={`field-type-select-${i}`} className={`${inputCls} w-36`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((ft) => <SelectItem key={ft} value={ft}>{typeLabel[ft]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs text-stone-600">
                  <Switch data-testid={`field-required-switch-${i}`} checked={f.required} onCheckedChange={(c) => update(i, { required: c })} />
                  {t("requiredField")}
                </label>
                <button data-testid={`field-delete-${i}`} onClick={() => remove(i)}
                  className="text-stone-400 hover:text-[#7F1D1D] transition-colors p-1.5">
                  <Trash2 size={15} strokeWidth={1.5} />
                </button>
              </div>
              {f.type === "dropdown" && (
                <input
                  data-testid={`field-options-input-${i}`}
                  className={`${inputCls} mt-2`}
                  placeholder={t("optionsHint")}
                  value={(f.options || []).join(", ")}
                  onChange={(e) => update(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                />
              )}
            </div>
          ))}
        </div>

        <button data-testid="add-field-button" onClick={() => setFields([...fields, newField()])}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900 border border-dashed border-stone-300 hover:border-stone-400 rounded-lg px-4 py-2.5 w-full justify-center transition-colors">
          <Plus size={15} strokeWidth={1.5} /> {t("addField")}
        </button>

        <div className="flex justify-end gap-3 mt-5">
          <button data-testid="cancel-form-builder-button" onClick={onClose}
            className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
          <button data-testid="save-form-builder-button" onClick={save} disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
